/**
 * DriverEntryRulesSection — Settings → Driver entry rules (OPEN-217, DEC-382; Firestone).
 *
 * Per job type, the checks the phone runs on what a driver types: "more than", "less than",
 * "outside a range" or "left empty", each a Warning (the driver confirms and carries on) or a
 * Block (the driver must fix it). The phone checks them offline; the server checks again at
 * completion and flags the ticket for review when something got past.
 *
 * Rules key on the job type FAMILY, so a later version of the job type keeps them. A rule whose
 * field the live version no longer lets the driver enter stays listed, marked, instead of
 * disappearing. Admin and billing staff edit (MANAGE_VALIDATION_RULES); the section is hidden
 * from everyone else.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useApiOpts } from '@lodex-pro/web-shared';
import {
  VALIDATION_RULE_FIELDS,
  suggestedValidationRulesFor,
  type SuggestedValidationRule,
  type ValidationRuleCheckKind,
  type ValidationRuleField,
  type ValidationRuleSeverity,
} from '@lodex-pro/shared/validation-rules';
import { fieldStyle, FormRow } from '../sites-operators/form-bits';
import {
  useServiceChargesAdmin,
  type AdminServiceCharge,
} from '../extra-services/use-service-charges-admin';
import {
  VALIDATION_RULES_QUERY_KEY,
  deleteValidationRule,
  saveValidationRule,
  useValidationRules,
  type EditorRule,
  type RuleFamily,
} from './use-validation-rules';
import {
  CHECK_LABELS,
  EMPTY_BOUND,
  boundsShown,
  buildRuleBody,
  checksFor,
  defaultMessage,
  describeCheck,
  fieldsFor,
  formFromRule,
  newRuleForm,
  previewMessage,
  serviceChargeFits,
  shapeLabel,
  stageHint,
  type BoundInput,
  type RuleForm,
} from './rule-form';

const subText: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };
const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid var(--color-border-primary)',
  marginBottom: '6px',
};
const btn: React.CSSProperties = {
  fontSize: '13px',
  padding: '6px 12px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-primary)',
  background: 'transparent',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};
const primaryBtn: React.CSSProperties = {
  ...btn,
  background: 'var(--color-accent-blue)',
  borderColor: 'var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
};
const tag: React.CSSProperties = {
  fontSize: '11px',
  padding: '1px 6px',
  borderRadius: '4px',
  border: '1px solid var(--color-border-primary)',
  color: 'var(--color-text-secondary)',
};
const formBox: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '12px',
  margin: '8px 0',
  borderRadius: 'var(--radius-card)',
  background: 'var(--color-bg-secondary)',
};

const SEVERITY_HELP: Record<ValidationRuleSeverity, string> = {
  WARNING: 'Warning: the driver sees the message and can confirm the value and carry on.',
  BLOCK: 'Block: the driver must change the value before saving.',
};

function SeverityTag({ severity }: { severity: ValidationRuleSeverity }): React.JSX.Element {
  return (
    <span
      style={{
        ...tag,
        color: severity === 'BLOCK' ? 'var(--color-danger)' : 'var(--color-warning)',
        borderColor: severity === 'BLOCK' ? 'var(--color-danger)' : 'var(--color-warning)',
      }}
    >
      {severity === 'BLOCK' ? 'Block' : 'Warning'}
    </span>
  );
}

/** The limit input(s) for a field's unit: feet + inches, H:MM, or one number. */
function BoundFields({
  id,
  label,
  field,
  value,
  onChange,
}: {
  id: string;
  label: string;
  field: ValidationRuleField;
  value: BoundInput;
  onChange: (v: BoundInput) => void;
}): React.JSX.Element {
  const unit = VALIDATION_RULE_FIELDS[field].unit;
  if (unit === 'IN') {
    return (
      <FormRow label={label} htmlFor={`${id}-ft`} required>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            id={`${id}-ft`}
            aria-label={`${label} feet`}
            inputMode="numeric"
            value={value.main}
            placeholder="0"
            onChange={(e): void => onChange({ ...value, main: e.target.value })}
            style={{ ...fieldStyle, width: '90px' }}
          />
          <span style={subText}>ft</span>
          <input
            aria-label={`${label} inches`}
            inputMode="decimal"
            value={value.inches}
            placeholder="0"
            onChange={(e): void => onChange({ ...value, inches: e.target.value })}
            style={{ ...fieldStyle, width: '90px' }}
          />
          <span style={subText}>in</span>
        </div>
      </FormRow>
    );
  }
  const suffix = unit === 'BBL' ? 'bbl' : unit === 'MIN' ? 'hours:minutes' : '';
  return (
    <FormRow label={label} htmlFor={id} required>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          id={id}
          aria-label={label}
          inputMode={unit === 'MIN' ? 'text' : 'decimal'}
          value={value.main}
          placeholder={unit === 'MIN' ? '16:00' : '0'}
          onChange={(e): void => onChange({ main: e.target.value, inches: '' })}
          style={{ ...fieldStyle, width: '140px' }}
        />
        {suffix !== '' && <span style={subText}>{suffix}</span>}
      </div>
    </FormRow>
  );
}

function RuleFormView({
  family,
  form,
  setForm,
  services,
  isEdit,
  saving,
  error,
  onSave,
  onCancel,
}: {
  family: RuleFamily;
  form: RuleForm;
  setForm: (f: RuleForm) => void;
  services: readonly AdminServiceCharge[];
  isEdit: boolean;
  saving: boolean;
  error: string | null;
  onSave: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const shape = family.shape;
  // An edited rule keeps its field in the list even if the live version dropped it.
  const fields = fieldsFor(shape);
  const fieldOptions = fields.includes(form.field) ? fields : [form.field, ...fields];
  const checks = checksFor(form.field, shape);
  const shown = boundsShown(form.checkKind);
  const fitServices = services.filter((s) => serviceChargeFits(shape, s));
  const chosenService = services.find((s) => s.id === form.serviceChargeId);
  const preview = previewMessage(form);
  const idBase = `rule-${family.familyId}`;

  const setField = (field: ValidationRuleField): void => {
    const nextChecks = checksFor(field, shape);
    setForm({
      ...form,
      field,
      checkKind: nextChecks.includes(form.checkKind) ? form.checkKind : 'GT',
      // The unit may change (inches vs bbl), so the typed limits don't carry over.
      min: EMPTY_BOUND,
      max: EMPTY_BOUND,
      serviceChargeId: field === 'SERVICE_QTY' ? form.serviceChargeId : '',
    });
  };
  const setSeverity = (severity: ValidationRuleSeverity): void => {
    // Swap the default message along with the severity, but never a message the user wrote.
    const isDefault = form.message === defaultMessage(form.severity);
    setForm({ ...form, severity, message: isDefault ? defaultMessage(severity) : form.message });
  };

  return (
    <div style={formBox} data-testid={`rule-form-${family.familyId}`}>
      <div style={{ fontWeight: 600 }}>{isEdit ? 'Edit rule' : 'New rule'}</div>

      <FormRow label="What the driver enters" htmlFor={`${idBase}-field`} required>
        <>
          <select
            id={`${idBase}-field`}
            aria-label="What the driver enters"
            value={form.field}
            onChange={(e): void => setField(e.target.value as ValidationRuleField)}
            style={fieldStyle}
          >
            {fieldOptions.map((f) => (
              <option key={f} value={f}>
                {VALIDATION_RULE_FIELDS[f].label}
              </option>
            ))}
          </select>
          <div style={subText}>{stageHint(form.field, chosenService?.stage ?? null)}</div>
        </>
      </FormRow>

      {form.field === 'SERVICE_QTY' && (
        <FormRow label="Extra service" htmlFor={`${idBase}-svc`} required>
          <select
            id={`${idBase}-svc`}
            aria-label="Extra service"
            value={form.serviceChargeId}
            onChange={(e): void => setForm({ ...form, serviceChargeId: e.target.value })}
            style={fieldStyle}
          >
            <option value="">Choose…</option>
            {fitServices.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </FormRow>
      )}

      <FormRow label="Check" htmlFor={`${idBase}-check`} required>
        <select
          id={`${idBase}-check`}
          aria-label="Check"
          value={form.checkKind}
          onChange={(e): void =>
            setForm({ ...form, checkKind: e.target.value as ValidationRuleCheckKind })
          }
          style={fieldStyle}
        >
          {checks.map((c) => (
            <option key={c} value={c}>
              {CHECK_LABELS[c]}
            </option>
          ))}
        </select>
      </FormRow>

      {shown.min && (
        <BoundFields
          id={`${idBase}-min`}
          label={form.checkKind === 'BETWEEN' ? 'Lowest allowed' : 'Limit'}
          field={form.field}
          value={form.min}
          onChange={(min): void => setForm({ ...form, min })}
        />
      )}
      {shown.max && (
        <BoundFields
          id={`${idBase}-max`}
          label={form.checkKind === 'BETWEEN' ? 'Highest allowed' : 'Limit'}
          field={form.field}
          value={form.max}
          onChange={(max): void => setForm({ ...form, max })}
        />
      )}

      <FormRow label="When it's broken">
        <>
          <div role="group" aria-label="When it's broken" style={{ display: 'flex', gap: '6px' }}>
            {(['WARNING', 'BLOCK'] as const).map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={form.severity === s}
                style={form.severity === s ? primaryBtn : btn}
                onClick={(): void => setSeverity(s)}
              >
                {s === 'WARNING' ? 'Warning' : 'Block'}
              </button>
            ))}
          </div>
          <div style={subText}>{SEVERITY_HELP[form.severity]}</div>
        </>
      </FormRow>

      <FormRow label="Message the driver sees" htmlFor={`${idBase}-msg`} required>
        <>
          <input
            id={`${idBase}-msg`}
            className="keep-case"
            value={form.message}
            onChange={(e): void => setForm({ ...form, message: e.target.value })}
            style={fieldStyle}
          />
          <div style={subText}>{'{value} is replaced by what the driver entered.'}</div>
        </>
      </FormRow>

      {preview !== null && (
        <div style={{ fontSize: '13px' }} data-testid="rule-preview">
          <span style={subText}>Driver sees: </span>
          {preview}
        </div>
      )}

      {error !== null && (
        <div role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="button" style={primaryBtn} disabled={saving} onClick={onSave}>
          {saving ? 'Saving…' : 'Save rule'}
        </button>
        <button type="button" style={btn} disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** A suggestion is already there when a rule checks the same field the same way. */
function alreadyHas(rules: readonly EditorRule[], s: SuggestedValidationRule): boolean {
  return rules.some((r) => r.field === s.field && r.checkKind === s.checkKind);
}

function SuggestedRules({
  family,
  rules,
  busy,
  onAdd,
  onClose,
}: {
  family: RuleFamily;
  rules: readonly EditorRule[];
  busy: boolean;
  onAdd: (chosen: readonly SuggestedValidationRule[]) => void;
  onClose: () => void;
}): React.JSX.Element {
  const offered = suggestedValidationRulesFor(family.shape).filter((s) => !alreadyHas(rules, s));
  const [chosen, setChosen] = React.useState<ReadonlySet<string>>(
    () => new Set(offered.map((s) => s.code)),
  );
  const toggle = (code: string): void =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  return (
    <div style={formBox} data-testid={`suggested-${family.familyId}`}>
      <div style={{ fontWeight: 600 }}>Suggested rules</div>
      {offered.length === 0 ? (
        <div style={subText}>Every suggestion that fits this job type is already a rule.</div>
      ) : (
        <>
          <div style={subText}>All Warnings. You can edit or delete them once added.</div>
          {offered.map((s) => (
            <label key={s.code} style={{ display: 'flex', gap: '8px', fontSize: '13px' }}>
              <input
                type="checkbox"
                checked={chosen.has(s.code)}
                onChange={(): void => toggle(s.code)}
              />
              <span>
                {VALIDATION_RULE_FIELDS[s.field].label}: {describeCheck(s).toLowerCase()}
              </span>
            </label>
          ))}
        </>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        {offered.length > 0 && (
          <button
            type="button"
            style={primaryBtn}
            disabled={busy || chosen.size === 0}
            onClick={(): void => onAdd(offered.filter((s) => chosen.has(s.code)))}
          >
            {busy ? 'Adding…' : 'Add selected'}
          </button>
        )}
        <button type="button" style={btn} disabled={busy} onClick={onClose}>
          {offered.length > 0 ? 'Cancel' : 'Close'}
        </button>
      </div>
    </div>
  );
}

type Open =
  | { readonly kind: 'rule'; readonly familyId: string; readonly ruleId: string | null }
  | { readonly kind: 'suggest'; readonly familyId: string }
  | null;

export function DriverEntryRulesSection(): React.JSX.Element {
  const opts = useApiOpts();
  const qc = useQueryClient();
  const { data, isLoading, isError } = useValidationRules(opts);
  const services = useServiceChargesAdmin(opts).data?.serviceCharges ?? [];

  const [open, setOpen] = React.useState<Open>(null);
  const [form, setForm] = React.useState<RuleForm | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const invalidate = (): Promise<void> =>
    qc.invalidateQueries({ queryKey: VALIDATION_RULES_QUERY_KEY });

  const close = (): void => {
    setOpen(null);
    setForm(null);
    setError(null);
  };
  const startNew = (family: RuleFamily): void => {
    setOpen({ kind: 'rule', familyId: family.familyId, ruleId: null });
    setForm(newRuleForm(family.shape));
    setError(null);
  };
  const startEdit = (rule: EditorRule): void => {
    setOpen({ kind: 'rule', familyId: rule.familyId, ruleId: rule.id });
    setForm(formFromRule(rule));
    setError(null);
  };

  const save = async (): Promise<void> => {
    if (open?.kind !== 'rule' || form === null) return;
    const built = buildRuleBody(form);
    if (!built.ok) {
      setError(built.error);
      return;
    }
    setSaving(true);
    setError(null);
    const body = open.ruleId === null ? { ...built.body, familyId: open.familyId } : built.body;
    const res = await saveValidationRule(opts, open.ruleId, body);
    setSaving(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    await invalidate();
    toast.success(open.ruleId === null ? 'Rule added' : 'Rule saved');
    close();
  };

  const remove = async (rule: EditorRule): Promise<void> => {
    if (!window.confirm('Delete this rule? Drivers stop seeing it after their next sync.')) return;
    const res = await deleteValidationRule(opts, rule.id);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    await invalidate();
    toast.success('Rule deleted');
  };

  const addSuggested = async (
    family: RuleFamily,
    chosen: readonly SuggestedValidationRule[],
  ): Promise<void> => {
    setSaving(true);
    let added = 0;
    let failure: string | null = null;
    // One at a time, so the per-family cap and the order are the API's to decide.
    for (const s of chosen) {
      const res = await saveValidationRule(opts, null, {
        familyId: family.familyId,
        field: s.field,
        checkKind: s.checkKind,
        minValue: s.minValue,
        maxValue: s.maxValue,
        message: s.message,
        severity: s.severity,
        serviceChargeId: null,
      });
      if (!res.ok) {
        failure = res.error;
        break;
      }
      added += 1;
    }
    setSaving(false);
    await invalidate();
    if (failure !== null) toast.error(`Added ${String(added)}; then: ${failure}`);
    else toast.success(`Added ${String(added)} rule${added === 1 ? '' : 's'}`);
    close();
  };

  if (isLoading) return <div style={subText}>Loading…</div>;
  if (isError || data === undefined) {
    return (
      <div role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)' }}>
        Couldn’t load the driver entry rules. Please reload the page.
      </div>
    );
  }

  const rulesOf = (familyId: string): EditorRule[] =>
    data.rules.filter((r) => r.familyId === familyId);
  const active = data.families.filter((f) => f.isActive);
  const inactive = data.families.filter((f) => !f.isActive);

  const renderFamily = (family: RuleFamily): React.JSX.Element => {
    const rules = rulesOf(family.familyId);
    const isOpen = open !== null && open.familyId === family.familyId;
    return (
      <div key={family.familyId} style={{ marginBottom: '18px' }} data-testid="rule-family">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600 }}>{family.label}</span>
          <span style={tag}>{shapeLabel(family.shape)}</span>
          <span style={{ flex: 1 }} />
          {!isOpen && (
            <>
              <button type="button" style={btn} onClick={(): void => startNew(family)}>
                + Add rule
              </button>
              <button
                type="button"
                style={btn}
                onClick={(): void => {
                  setOpen({ kind: 'suggest', familyId: family.familyId });
                  setForm(null);
                  setError(null);
                }}
              >
                Add suggested rules
              </button>
            </>
          )}
        </div>

        {isOpen && open.kind === 'rule' && open.ruleId === null && form !== null && (
          <RuleFormView
            family={family}
            form={form}
            setForm={setForm}
            services={services}
            isEdit={false}
            saving={saving}
            error={error}
            onSave={(): void => void save()}
            onCancel={close}
          />
        )}
        {isOpen && open.kind === 'suggest' && (
          <SuggestedRules
            family={family}
            rules={rules}
            busy={saving}
            onAdd={(chosen): void => void addSuggested(family, chosen)}
            onClose={close}
          />
        )}

        <div style={{ marginTop: '6px' }}>
          {rules.length === 0 && (
            <div style={subText}>No rules: drivers can enter anything the job type allows.</div>
          )}
          {rules.map((r) =>
            open?.kind === 'rule' && open.ruleId === r.id && form !== null ? (
              <RuleFormView
                key={r.id}
                family={family}
                form={form}
                setForm={setForm}
                services={services}
                isEdit
                saving={saving}
                error={error}
                onSave={(): void => void save()}
                onCancel={close}
              />
            ) : (
              <div key={r.id} style={{ ...rowStyle, opacity: r.applies ? 1 : 0.6 }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <div>
                    <span style={{ fontWeight: 600 }}>
                      {VALIDATION_RULE_FIELDS[r.field].label}
                      {r.field === 'SERVICE_QTY' && r.serviceChargeLabel !== null
                        ? `: ${r.serviceChargeLabel}`
                        : ''}
                    </span>{' '}
                    <span style={subText}>· {describeCheck(r)}</span>{' '}
                    <SeverityTag severity={r.severity} />
                  </div>
                  <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                    “{r.message}”
                  </span>
                  {!r.applies && (
                    <span style={{ ...subText, color: 'var(--color-warning)' }}>
                      Doesn’t apply to the current version of this job type: drivers aren’t asked
                      for this anymore.
                    </span>
                  )}
                </div>
                <button type="button" style={btn} onClick={(): void => startEdit(r)}>
                  Edit
                </button>
                <button type="button" style={btn} onClick={(): void => void remove(r)}>
                  Delete
                </button>
              </div>
            ),
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ maxWidth: '720px' }}>
      <p style={{ marginTop: 0, fontSize: '14px', color: 'var(--color-text-secondary)' }}>
        Checks on what drivers type, per job type. A Warning asks the driver to confirm; a Block
        makes them fix it. The phone checks them even with no signal, and a ticket that gets past
        one is flagged for review.
      </p>
      {active.length === 0 && <div style={subText}>No active job types yet.</div>}
      {active.map(renderFamily)}
      {inactive.length > 0 && (
        <details style={{ marginTop: '12px' }}>
          <summary style={{ cursor: 'pointer', fontSize: '13px' }}>
            Inactive job types ({inactive.length})
          </summary>
          <div style={{ marginTop: '10px' }}>{inactive.map(renderFamily)}</div>
        </details>
      )}
    </div>
  );
}
